import React, { useEffect } from 'react';
import fetch from 'node-fetch';
import {
  FlagProvider,
  useUnleashContext,
  useUnleashClient,
  useFlagsStatus,
  UnleashClient,
  InMemoryStorageProvider,
  useFlags,
  useFlag,
  useVariant,
} from '@unleash/proxy-client-react';
import isServer from '../utils/isServer';

const config = {
  url: process.env.REACT_APP_UNLEASH_URL,
  clientKey: process.env.REACT_APP_UNLEASH_CLIENT_KEY,
  appName: 'codeSpark web',
  environment: process.env.NODE_ENV,
  disableRefresh: true,
};

// Provide alternatives to fetch and localstorage when on server so error related
// to windows not existing does not occur
const ssrOption = isServer() ? { fetch, storageProvider: new InMemoryStorageProvider() } : {};

// Create client separately and expose for analytics use outside of react
export const featureFlagClient = new UnleashClient({
  ...config,
  ...ssrOption,
});

export const FeatureFlagsProvider = ({ children }) => {
  // We're only running feature flags on the browser so wrap in useEffect so it defers
  // loading to client-side
  useEffect(() => {
    featureFlagClient.start();

    return function cleanup() {
      if (featureFlagClient) {
        featureFlagClient.stop();
      }
    };
  }, []);

  return (
    <FlagProvider unleashClient={featureFlagClient} startClient={false}>
      {children}
    </FlagProvider>
  );
};

// Hook for functions that return class components that cannot use the unleash react sdk hooks
// For example: Home component
export const useFlagsHooksWrapper = () => {
  const flags = useFlags();
  const client = useUnleashClient();

  const updateUnleashContext = useUnleashContext();
  const { flagsError, flagsReady } = useFlagsStatus();

  const getFlag = featureName => flags.find(flag => flag.name === featureName);
  const flagEnabled = featureName => isFlagEnabled(getFlag(featureName));
  const getVariant = variant => getFlag(variant).variant;
  const variantEnabled = variant => isVariantEnabled(getVariant(variant));

  return {
    flagEnabled,
    getFlag,
    getVariant,
    variantEnabled,
    flagsError,
    flagsReady,
    updateUnleashContext,
    client,
  };
};

/**
 * Checks feature flags are ready for use and there are no errors.
 * @returns {boolean} true if flags are ready to for use, false otherwise.
 */
export const useFlagsReady = () => {
  const { flagsError, flagsReady } = useFlagsStatus();

  // bail if something goes wrong or flags are not ready.
  if (flagsError || !flagsReady) return false;

  return true;
};

/**
 * Checks if a flag is enabled.
 * @param {string} flagName the name of the flag to check.
 * @returns {boolean} true if the flag is enabled, false otherwise.
 */
export const useCodeSparkFlag = (flagName) => {
  const isReady = useFlagsReady();
  const isEnabled = useFlag(flagName);

  // bail if something goes wrong or flags are not ready.
  if (!isReady) return false;

  return isEnabled;
};

/**
 * Checks if a list of flags and returns whether they are enabled in the same order
 * @param {array} flagNames list of flags to check.
 * @returns {array} list of flags with their name and status
 */
export const useCodeSparkFlags = (flagNames) => {
  const isReady = useFlagsReady();
  const flags = useFlags();

  // bail if something goes wrong or flags are not ready.
  if (!isReady) return [];

  return flagNames.map(flagName => {
    const flag = flags.find(f => f.name === flagName);
    // If no such flag exists, just return false
    const enabled = flag ? flag.enabled : false;
    return {
      name: flagName,
      enabled,
    };
  });
};

/**
 * Checks if a flag is enabled and returns the variant object.
 * @param {string} flagName the name of the flag to check.
 * @returns {string|object} the variant object if the flag is enabled, false otherwise.
 */
export const useCodeSparkVariant = (flagName) => {
  const { flagsError, flagsReady } = useFlagsStatus();
  const variant = useVariant(flagName);

  // bail if something goes wrong or flags are not ready.
  if (flagsError || !flagsReady) return false;

  return variant;
};

// Wrapper for class components using render pattern since the react unleash sdk uses hooks
export const UnleashClassFlagProvider = ({ render }) => {
  const flagSDKProps = useFlagsHooksWrapper();

  return render(flagSDKProps);
};

export const isFlagEnabled = toggle => toggle.enabled;
export const isVariantEnabled = variant => variant && variant.enabled;

export const getEnabledExperiments = flags =>
  flags.filter(flag => isFlagEnabled(flag) && isVariantEnabled(flag.variant));

export const getVariantPayload = ({ payload }) => {
  if (!payload) {
    return undefined;
  }

  let variantPayload;
  if (payload.type === 'json') {
    variantPayload = JSON.parse(payload.value);
  }

  return variantPayload;
};



// WEBPACK FOOTER //
// ./src/featureFlags/index.js